import {
  isColumnarSheet,
  isFreeformSheet,
  isPivotSheet,
  type WorkbookColumnarSheet,
  type WorkbookDefinition,
  type WorkbookFreeformSheet,
  type WorkbookOutput,
  type WorkbookPivotSheet,
  type WorkbookSheet,
} from '@erp/shared-types';
import { applyOutlineOp, type OutlineOp } from '@erp/workbooks';

type ProcessStep = WorkbookDefinition['processSteps'][number];
export type Update<T> = (current: T) => T;
export type StepPatch = Partial<Pick<ProcessStep, 'title' | 'instructions'>>;
/** An accepted SOP update: the rewritten text and where it came from. */
export type SopUpdatePatch = StepPatch &
  Pick<ProcessStep, 'instructions'> &
  Required<Pick<ProcessStep, 'provenance'>>;
/**
 * How a commit marks review status: `applied` steps are in sync with their
 * settings (cleared), `restored` steps keep the status they are written with.
 */
export interface CommitMarks {
  applied?: readonly string[];
  restored?: readonly string[];
}

/** Writes to one Process step. Each call resolves the step against the latest Definition. */
export interface StepEdits {
  /** The user's edit; it drops the text's AI provenance. */
  update: (patch: StepPatch) => void;
  /** Applies an accepted SOP update: the text now describes its settings. */
  applySopUpdate: (patch: SopUpdatePatch) => void;
  /** Puts the step back exactly as it was (an undo), review status included. */
  restore: (step: ProcessStep) => void;
  move: (direction: 'up' | 'down') => void;
  indent: () => void;
  outdent: () => void;
  /** Removes the step; its children move up to its parent. */
  remove: () => void;
}

/**
 * What panels write through. Every handle applies to the latest Definition when it is
 * called, never to the one captured at render, so a stale panel cannot undo a newer
 * edit. Whole-value replace is reserved for the agent port and restore.
 */
export interface WorkbookEdits {
  workbook: { update: (fn: Update<WorkbookDefinition>) => void };
  sheet: (id: string) => { update: (fn: Update<WorkbookSheet>) => void };
  /** Writes to one columnar Worksheet; does nothing while it is another kind. */
  columnarSheet: (id: string) => {
    update: (fn: Update<WorkbookColumnarSheet>) => void;
  };
  /** Writes to one pivot Worksheet; does nothing while it is another kind. */
  pivotSheet: (id: string) => {
    update: (fn: Update<WorkbookPivotSheet>) => void;
  };
  /** Writes to one freeform sheet; does nothing while it is another kind. */
  freeformSheet: (id: string) => {
    update: (fn: Update<WorkbookFreeformSheet>) => void;
  };
  /** Writes to one Output; removing it also unlinks it from every Instruction. */
  output: (id: string) => {
    update: (fn: Update<WorkbookOutput>) => void;
    remove: () => void;
  };
  step: (id: string) => StepEdits;
  /** Inserts an empty instruction linked to `itemIds` (a Data note when they are all data) and returns its id. */
  insertStep: (
    parentId: string | null,
    afterId?: string,
    itemIds?: readonly string[],
  ) => string;
}

function replaceStep(
  definition: WorkbookDefinition,
  id: string,
  replace: (step: ProcessStep) => ProcessStep,
): WorkbookDefinition {
  if (!definition.processSteps.some((step) => step.id === id))
    return definition;
  return {
    ...definition,
    processSteps: definition.processSteps.map((step) =>
      step.id === id ? replace(step) : step,
    ),
  };
}

/** The user's own words replace the AI's, so the text loses its provenance. */
function userPatch(step: ProcessStep, patch: StepPatch): ProcessStep {
  const next = { ...step, ...patch };
  if (patch.title === undefined && patch.instructions === undefined)
    return next;
  const { provenance: _dropped, ...rest } = next;
  return rest;
}

/** Builds the handles over `commit`, which applies an update to the latest Definition. */
/**
 * {@link workbookEdits} for a component or hook: its commit may read refs, which
 * is safe because the edits call it only from handlers, never during render.
 */
export function useWorkbookEdits(
  commit: (update: Update<WorkbookDefinition>, marks?: CommitMarks) => void,
): WorkbookEdits {
  return workbookEdits(commit);
}

export function workbookEdits(
  commit: (update: Update<WorkbookDefinition>, marks?: CommitMarks) => void,
): WorkbookEdits {
  const apply = (op: OutlineOp) =>
    commit((definition) => applyOutlineOp(definition, op));
  const updateSheet = (id: string, fn: Update<WorkbookSheet>) =>
    commit((definition) =>
      definition.sheets.some((sheet) => sheet.id === id)
        ? {
            ...definition,
            sheets: definition.sheets.map((sheet) =>
              sheet.id === id ? fn(sheet) : sheet,
            ),
          }
        : definition,
    );
  return {
    workbook: { update: commit },
    sheet: (id) => ({ update: (fn) => updateSheet(id, fn) }),
    columnarSheet: (id) => ({
      update: (fn) =>
        updateSheet(id, (sheet) =>
          isColumnarSheet(sheet) ? fn(sheet) : sheet,
        ),
    }),
    pivotSheet: (id) => ({
      update: (fn) =>
        updateSheet(id, (sheet) => (isPivotSheet(sheet) ? fn(sheet) : sheet)),
    }),
    freeformSheet: (id) => ({
      update: (fn) =>
        updateSheet(id, (sheet) =>
          isFreeformSheet(sheet) ? fn(sheet) : sheet,
        ),
    }),
    output: (id) => ({
      update: (fn) =>
        commit((definition) =>
          definition.outputs.some((output) => output.id === id)
            ? {
                ...definition,
                outputs: definition.outputs.map((output) =>
                  output.id === id ? fn(output) : output,
                ),
              }
            : definition,
        ),
      remove: () =>
        commit((definition) => {
          if (!definition.outputs.some((output) => output.id === id))
            return definition;
          const detached = applyOutlineOp(definition, {
            type: 'detachItem',
            itemId: id,
          });
          return {
            ...detached,
            outputs: detached.outputs.filter((output) => output.id !== id),
          };
        }),
    }),
    step: (id) => ({
      update: (patch) =>
        commit((definition) =>
          replaceStep(definition, id, (step) => userPatch(step, patch)),
        ),
      applySopUpdate: (patch) =>
        commit(
          (definition) =>
            replaceStep(definition, id, (step) => ({ ...step, ...patch })),
          { applied: [id] },
        ),
      restore: (previous) =>
        commit((definition) => replaceStep(definition, id, () => previous), {
          restored: [id],
        }),
      move: (direction) => apply({ type: 'move', id, direction }),
      indent: () => apply({ type: 'indent', id }),
      outdent: () => apply({ type: 'outdent', id }),
      remove: () => apply({ type: 'remove', id }),
    }),
    insertStep(parentId, afterId, itemIds) {
      const id = crypto.randomUUID();
      apply({ type: 'insert', id, parentId, afterId, itemIds });
      return id;
    },
  };
}
